import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  Modal,
  notify,
  RowInput,
} from '@affine/component';
import type {
  DialogComponentProps,
  WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { FolderShareService } from '@affine/core/modules/folder-share';
import {
  type Member,
  MemberSearchService,
} from '@affine/core/modules/permissions';
import { UserFriendlyError } from '@affine/error';
import { DocRole, WorkspaceMemberStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import { debounce } from 'lodash-es';
import {
  type CompositionEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as inviteStyles from '../../../modules/share-menu/view/share-menu/invite-member-editor/invite-member-editor.css';
import { MemberItem } from '../../../modules/share-menu/view/share-menu/invite-member-editor/member-item';
import { SelectedMemberItem } from '../../../modules/share-menu/view/share-menu/invite-member-editor/selected-member-item';

const getRoleName = (t: ReturnType<typeof useI18n>, role: DocRole) => {
  switch (role) {
    case DocRole.Manager:
      return t['com.affine.share-menu.option.permission.can-manage']();
    case DocRole.Editor:
      return t['com.affine.share-menu.option.permission.can-edit']();
    case DocRole.Commenter:
      return t['com.affine.share-menu.option.permission.can-comment']();
    case DocRole.Reader:
    default:
      return t['com.affine.share-menu.option.permission.can-read']();
  }
};

export const FolderShareDialog = ({
  close,
  folderId,
  folderName,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['folder-share']>) => {
  const t = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const folderShareService = useService(FolderShareService);
  const memberSearchService = useService(MemberSearchService);
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [role, setRole] = useState<DocRole>(DocRole.Reader);
  const [searchText, setSearchText] = useState('');
  const [focused, setFocused] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [policyEnabled, setPolicyEnabled] = useState(
    !!folderShareService.getPolicy(folderId)
  );

  const result = useLiveData(memberSearchService.result$);
  const isLoading = useLiveData(memberSearchService.isLoading$);
  const docCount = useMemo(
    () => folderShareService.collectDocIds(folderId).length,
    [folderId, folderShareService]
  );

  useEffect(() => {
    memberSearchService.reset();
    memberSearchService.loadMore();
  }, [memberSearchService]);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => memberSearchService.search(value), 300),
    [memberSearchService]
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (!composing) {
        debouncedSearch(value);
      }
    },
    [composing, debouncedSearch]
  );

  const handleCompositionStart: CompositionEventHandler<HTMLInputElement> =
    useCallback(() => {
      setComposing(true);
    }, []);

  const handleCompositionEnd: CompositionEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        setComposing(false);
        debouncedSearch(e.currentTarget.value);
      },
      [debouncedSearch]
    );

  const handleClickMember = useCallback(
    (member: Member) => {
      setSelectedMembers(prev => {
        if (prev.some(item => item.id === member.id)) {
          return prev;
        }
        return [...prev, member];
      });
      setSearchText('');
      memberSearchService.search('');
      inputRef.current?.focus();
    },
    [memberSearchService]
  );

  const activeMembers = useMemo(
    () =>
      result.filter(member => member.status === WorkspaceMemberStatus.Accepted),
    [result]
  );

  const onClearPolicy = useCallback(() => {
    folderShareService.clearPolicy(folderId);
    setPolicyEnabled(false);
    notify.success({
      title: t['com.affine.folderShare.autoSharePolicyDisabled'](),
    });
  }, [folderId, folderShareService]);

  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const selectedMemberIds = selectedMembers.map(member => member.id);
      const shareResult = await folderShareService.shareFolder(
        folderId,
        selectedMemberIds,
        role
      );
      setPolicyEnabled(true);
      notify.success({
        title: `Shared ${shareResult.successDocIds.length}/${shareResult.docIds.length} documents`,
      });
      if (shareResult.failedDocIds.length) {
        notify.error({
          title: `${shareResult.failedDocIds.length} documents failed to share`,
        });
      }
      close();
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({ title: t[`error.${err.name}`](err.data) });
    } finally {
      setSharing(false);
    }
  }, [close, folderId, folderShareService, role, selectedMembers, t]);

  return (
    <Modal
      open
      onOpenChange={() => close()}
      withoutCloseButton
      width={480}
      contentOptions={{
        style: {
          padding: 16,
          background: cssVar('backgroundPrimaryColor'),
        },
      }}
    >
      <div className={inviteStyles.containerStyle}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          {t['com.affine.folderShare.title']()}
        </div>
        <div style={{ color: cssVar('textSecondaryColor'), fontSize: 13 }}>
          {t['com.affine.folderShare.description']({
            folderName,
            docCount: String(docCount),
          })}
        </div>
        {policyEnabled ? (
          <div style={{ color: cssVar('textSecondaryColor'), fontSize: 13 }}>
            {t['com.affine.folderShare.autoShareHint']()}
          </div>
        ) : null}

        <div
          className={`${inviteStyles.InputContainer} ${focused ? 'focus' : ''}`}
        >
          <div className={inviteStyles.inlineMembersContainer}>
            {selectedMembers.map((member, idx) => (
              <SelectedMemberItem
                key={member.id}
                idx={idx}
                member={member}
                onRemoved={() =>
                  setSelectedMembers(prev =>
                    prev.filter(item => item.id !== member.id)
                  )
                }
              />
            ))}
            <RowInput
              ref={inputRef}
              value={searchText}
              onChange={handleValueChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoFocus
              className={inviteStyles.searchInput}
              placeholder={
                selectedMembers.length
                  ? ''
                  : t['com.affine.folderShare.searchMembers']()
              }
            />
          </div>
          <Menu
            contentOptions={{ align: 'end' }}
            items={
              <>
                <MenuItem
                  onSelect={() => setRole(DocRole.Reader)}
                  selected={role === DocRole.Reader}
                >
                  {t['com.affine.share-menu.option.permission.can-read']()}
                </MenuItem>
                <MenuItem
                  onSelect={() => setRole(DocRole.Commenter)}
                  selected={role === DocRole.Commenter}
                >
                  {t['com.affine.share-menu.option.permission.can-comment']()}
                </MenuItem>
                <MenuItem
                  onSelect={() => setRole(DocRole.Editor)}
                  selected={role === DocRole.Editor}
                >
                  {t['com.affine.share-menu.option.permission.can-edit']()}
                </MenuItem>
                <MenuItem
                  onSelect={() => setRole(DocRole.Manager)}
                  selected={role === DocRole.Manager}
                >
                  {t['com.affine.share-menu.option.permission.can-manage']()}
                </MenuItem>
              </>
            }
          >
            <MenuTrigger className={inviteStyles.menuTriggerStyle}>
              {getRoleName(t, role)}
            </MenuTrigger>
          </Menu>
        </div>

        <div className={inviteStyles.resultContainer}>
          {searchText && isLoading ? <Loading /> : null}
          {searchText && !isLoading && !activeMembers.length ? (
            <div className={inviteStyles.noFound}>
              {t['com.affine.folderShare.noMembers']()}
            </div>
          ) : null}
          {searchText
            ? activeMembers.map(member => (
                <MemberItem
                  key={member.id}
                  member={member}
                  onSelect={handleClickMember}
                />
              ))
            : null}
        </div>

        <div className={inviteStyles.footerStyle}>
          <div />
          <div className={inviteStyles.buttonsContainer}>
            {policyEnabled ? (
              <Button className={inviteStyles.button} onClick={onClearPolicy}>
                {t['com.affine.folderShare.disableAutoShare']()}
              </Button>
            ) : null}
            <Button className={inviteStyles.button} onClick={() => close()}>
              {t['Cancel']()}
            </Button>
            <Button
              className={inviteStyles.button}
              variant="primary"
              disabled={!selectedMembers.length || !docCount || sharing}
              onClick={() => void onShare()}
            >
              {sharing
                ? t['com.affine.folderShare.sharing']()
                : t['com.affine.folderShare.shareDocs']({
                    count: String(docCount),
                  })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
